import { NetworkStatus } from "@apollo/client";
import { useEffect, useRef } from "react";

export function aroundIdQueryVariable(
  aroundId: string | undefined,
  paginationVariables: { after?: string; before?: string; first?: number; last?: number }
) {
  return paginationVariables.after || paginationVariables.before ? undefined : aroundId;
}

export function aroundIdMissingFromLoadedPage(
  aroundId: string | undefined,
  options: { loading: boolean; loadedNodeIds?: string[] }
) {
  if (!aroundId || options.loading || !options.loadedNodeIds) {
    return false;
  }

  return !options.loadedNodeIds.includes(aroundId);
}

export type AroundIdMissingAction = "wait" | "found" | "clear-filters" | "give-up";

export function aroundIdQueryInFlight(networkStatus?: number) {
  return (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch
  );
}

export function aroundIdTableLoading(loading: boolean, hasQueryResult: boolean) {
  return loading && !hasQueryResult;
}

export function aroundIdMissingAction(
  aroundId: string | undefined,
  options: {
    loading: boolean;
    loadedNodeIds?: string[];
    hasBlockingFilters: boolean;
    networkStatus?: number;
  }
): AroundIdMissingAction {
  if (!aroundId || options.loading || aroundIdQueryInFlight(options.networkStatus)) {
    return "wait";
  }

  if (options.loadedNodeIds?.includes(aroundId)) {
    return "found";
  }

  if (!options.loadedNodeIds && options.networkStatus !== NetworkStatus.error) {
    return "wait";
  }

  return options.hasBlockingFilters ? "clear-filters" : "give-up";
}

export type AroundIdResetKeyChange = "none" | "keep" | "clear";

export function aroundIdResetKeyChange(
  previousResetKey: { current: string | undefined },
  resetKey: string | undefined,
  keepThroughNextReset: { current: boolean }
): AroundIdResetKeyChange {
  if (previousResetKey.current === resetKey) {
    return "none";
  }

  previousResetKey.current = resetKey;
  if (keepThroughNextReset.current) {
    return "keep";
  }

  return "clear";
}

interface UseAroundIdMissingPageOptions {
  aroundId: string | undefined;
  loading: boolean;
  loadedNodeIds?: string[];
  currentPage?: number;
  hasBlockingFilters: boolean;
  networkStatus?: number;
  keepAroundIdThroughNextReset: (keep?: boolean) => void;
  clearFilters: () => void;
  clearAroundId: () => void;
  resetPagination: () => void;
  syncCurrentPage: (page: number) => void;
}

export function useAroundIdMissingPage({
  aroundId,
  loading,
  loadedNodeIds,
  currentPage,
  hasBlockingFilters,
  networkStatus,
  keepAroundIdThroughNextReset,
  clearFilters,
  clearAroundId,
  resetPagination,
  syncCurrentPage
}: UseAroundIdMissingPageOptions) {
  const retriedAroundIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (!aroundId) {
      retriedAroundIdRef.current = undefined;
      keepAroundIdThroughNextReset(false);
      return;
    }

    const action = aroundIdMissingAction(aroundId, {
      loading,
      loadedNodeIds,
      hasBlockingFilters,
      networkStatus
    });

    if (action === "wait") {
      return;
    }

    if (action === "found") {
      keepAroundIdThroughNextReset(false);
      if (typeof currentPage === "number") {
        syncCurrentPage(currentPage);
      }
      return;
    }

    if (action === "clear-filters") {
      if (retriedAroundIdRef.current === aroundId) {
        return;
      }
      retriedAroundIdRef.current = aroundId;
      keepAroundIdThroughNextReset();
      clearFilters();
      resetPagination();
      return;
    }

    keepAroundIdThroughNextReset(false);
    clearAroundId();
    resetPagination();
  }, [
    aroundId,
    clearAroundId,
    clearFilters,
    currentPage,
    hasBlockingFilters,
    keepAroundIdThroughNextReset,
    loadedNodeIds,
    loading,
    networkStatus,
    resetPagination,
    syncCurrentPage
  ]);
}
